import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';

const luminance = (hex) => {
  if (!hex || typeof hex !== 'string') return 0.5;
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  if (Number.isNaN(bigint)) return 0.5;
  const r = ((bigint >> 16) & 255) / 255;
  const g = ((bigint >> 8) & 255) / 255;
  const b = (bigint & 255) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Brand logo with a 5-layer fallback chain designed to always show the REAL
 * brand logo (never letter-tiles unless every source has failed):
 *   1. brand.logoUrl (curated override)
 *   2. Simple Icons colored SVG (theme-aware)
 *   3. Clearbit high-res raster logo (works in browsers)
 *   4. Uplead raster logo
 *   5. Google Favicon 128px (works for any domain)
 *   6. Premium letter-tile (last-resort, never seen in practice for major brands)
 *
 * The parent card can add a white "pill" background around this component
 * so raster logos (which usually have white/transparent backgrounds) look
 * intentional against colored gradient tiles — Bitrefill-style.
 */
const BrandImage = ({ brand, className = '', style = {}, tileClassName = '', letters }) => {
  const { isDark } = useTheme();
  const [step, setStep] = useState(0);

  if (!brand) return null;

  const brandColor = brand.color || '#8247E5';
  const iconColor = isDark && luminance(brandColor) < 0.35 ? '#E9D5FF' : brandColor;

  const sources = [];
  if (brand.logoUrl) sources.push(brand.logoUrl);
  if (brand.slug) sources.push(`https://cdn.simpleicons.org/${brand.slug}/${iconColor.replace('#', '')}`);
  if (brand.domain) sources.push(`https://logo.clearbit.com/${brand.domain}`);
  if (brand.domain) sources.push(`https://logo.uplead.com/${brand.domain}`);
  if (brand.domain) sources.push(`https://www.google.com/s2/favicons?domain=${brand.domain}&sz=128`);

  if (step >= sources.length) {
    const text = letters || (brand.name || '?')
      .split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
    return (
      <div
        className={`flex items-center justify-center font-bold font-display ${className} ${tileClassName}`}
        style={{
          color: '#ffffff',
          background: `linear-gradient(135deg, ${brandColor} 0%, ${brandColor}CC 100%)`,
          textShadow: '0 2px 6px rgba(0,0,0,0.25)',
          letterSpacing: '-0.03em',
          ...style,
        }}
      >
        {text}
      </div>
    );
  }

  return (
    <img
      src={sources[step]}
      alt={brand.name || 'brand logo'}
      className={className}
      style={style}
      loading="lazy"
      decoding="async"
      onError={() => setStep(s => s + 1)}
    />
  );
};

export default BrandImage;
